import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, ExternalLink, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import PageHero from "@/components/PageHero";
import WaitlistCTA from "@/components/home/WaitlistCTA";
import SEO from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

const COVERAGE = [
  {
    outlet: "TechCrunch",
    date: "Nov 2025",
    kicker: "Fintech · Africa",
    title: "Instantxfer CashApp wants to be the WhatsApp of cross-border money.",
    href: "#",
  },
  {
    outlet: "Sifted",
    date: "Oct 2025",
    kicker: "Startups",
    title: "Amsterdam's Instantxfer CashApp raises quietly to fix the £45bn remittance leak.",
    href: "#",
  },
  {
    outlet: "TechCabal",
    date: "Sep 2025",
    kicker: "Africa",
    title: "How Instantxfer CashApp's Lagos-Nairobi corridor settles under three minutes.",
    href: "#",
  },
  {
    outlet: "Rest of World",
    date: "Aug 2025",
    kicker: "Ecosystem",
    title: "The scan-to-pay handle: an African take on Bancontact.",
    href: "#",
  },
  {
    outlet: "The Africa Report",
    date: "Jul 2025",
    kicker: "Policy",
    title: "What lower remittance fees would actually buy the continent.",
    href: "#",
  },
  {
    outlet: "Wired",
    date: "Jun 2025",
    kicker: "Design",
    title: "Inside the iOS-native fintech built for a mother in Kigali.",
    href: "#",
  },
];

const ESSAYS = [
  {
    n: "01",
    title: "Remittance is love with a timestamp.",
    excerpt:
      "The moment the money lands is a moment of relief for someone. The delay is what we've been optimising against — and the cost is what we've been paying it in.",
    read: "6 min read",
  },
  {
    n: "02",
    title: "Why a handle is more honest than an IBAN.",
    excerpt:
      "IBANs describe accounts; handles describe people. Instantxfer CashApp bets that the way remittance actually works — mothers, uncles, kids — deserves a name, not a routing number.",
    read: "4 min read",
  },
  {
    n: "03",
    title: "The two‑day wire is a design bug.",
    excerpt:
      "There is no correspondent-bank law of physics. Cross-border latency is a business choice — and one we're refusing to inherit.",
    read: "5 min read",
  },
  {
    n: "04",
    title: "Fees printed on the ticket.",
    excerpt:
      "A trust essay on why we show the number to the cent before you tap send. Trust compounds; opacity taxes.",
    read: "3 min read",
  },
];

const REMITTANCE_STATS = [
  {
    kpi: "$95B",
    label: "Sent to sub-Saharan Africa in 2024",
    source: "World Bank Migration & Remittance Brief 2024",
  },
  {
    kpi: "7.4%",
    label: "Average fee for a $200 SSA transfer",
    source: "World Bank Remittance Prices Worldwide Q4 2024",
  },
  {
    kpi: "68%",
    label: "SSA share via mobile money by 2027 (est.)",
    source: "GSMA State of the Industry, Mobile Money 2024",
  },
  {
    kpi: "3.0%",
    label: "UN SDG target fee by 2030",
    source: "United Nations SDG 10.c.1",
  },
];

export default function Press() {
  const { t } = useTranslation();
  return (
    <>
      <SEO
        slug="press"
        title="Press · Instantxfer CashApp — Read the story as we write it"
        description="Press room, remittance data and founder essays for Instantxfer CashApp. Download the media kit, book a founder for interview, and see the sources behind our numbers."
      />
      <PageHero
        eyebrow={t("pageHero.press.eyebrow")}
        title={t("pageHero.press.title")}
        italic={t("pageHero.press.italic")}
        body={t("pageHero.press.body")}
        testId="press-hero"
      />

      {/* Media coverage grid */}
      <section className="pb-24 md:pb-32 bg-canvas" data-testid="press-coverage">
        <div className="container-x">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="label-eyebrow">In the news</div>
              <h2 className="mt-3 font-display font-medium text-forest text-4xl sm:text-5xl tracking-tightest">
                What people are saying.
              </h2>
            </div>
            <a
              href="mailto:press@instantxfer.com"
              className="btn-ghost"
              data-testid="press-media-inquiry"
            >
              press@instantxfer.com
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {COVERAGE.map((c, i) => (
              <motion.a
                key={c.outlet + i}
                href={c.href}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.06 }}
                className="card-flat p-6 md:p-7 flex flex-col justify-between hover:border-forest/40 transition-colors group"
                data-testid={`press-card-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-lg text-forest tracking-tight">
                    {c.outlet}
                  </span>
                  <ExternalLink className="w-4 h-4 text-ink/30 group-hover:text-forest transition-colors" />
                </div>
                <div className="flex items-center gap-3 mt-3 text-[10px] tracking-[0.22em] uppercase text-ink/50">
                  <span>{c.kicker}</span>
                  <span className="w-1 h-1 rounded-full bg-ink/25" />
                  <span>{c.date}</span>
                </div>
                <h3 className="mt-6 font-display font-medium text-forest text-xl md:text-2xl leading-tight tracking-tight">
                  &ldquo;{c.title}&rdquo;
                </h3>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Founder essays */}
      <section className="py-24 md:py-32 bg-surface" data-testid="press-essays">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-12">
            <div className="md:col-span-8">
              <div className="label-eyebrow">Founder essays</div>
              <h2 className="mt-3 font-display font-medium text-forest text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
                What we think, <span className="italic font-serif">unfiltered.</span>
              </h2>
            </div>
            <div className="md:col-span-4 text-ink/70">
              Longer notes from the founding team on the ideas that shape Instantxfer CashApp. Slow reading, not scrollable content.
            </div>
          </div>

          <div className="divide-y divide-forest/10 border-y border-forest/10">
            {ESSAYS.map((e, i) => (
              <motion.article
                key={e.n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.05 }}
                className="grid grid-cols-12 gap-6 py-10 md:py-12"
                data-testid={`press-essay-${e.n}`}
              >
                <div className="col-span-12 md:col-span-2 font-serif italic text-5xl md:text-6xl text-forest/85 leading-none">
                  {e.n}
                </div>
                <div className="col-span-12 md:col-span-6">
                  <h3 className="font-display font-medium text-forest text-2xl md:text-4xl leading-[1.05] tracking-tight">
                    {e.title}
                  </h3>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <p className="text-ink/70 leading-relaxed">{e.excerpt}</p>
                  <div className="mt-3 text-[11px] tracking-[0.22em] uppercase text-ink/45">
                    {e.read} · essay
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Remittance data */}
      <section className="py-24 md:py-32 bg-forest text-canvas noise" data-testid="press-data">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-14">
            <div className="md:col-span-8">
              <div className="label-eyebrow !text-lime">The remittance stack</div>
              <h2 className="mt-3 font-display font-medium text-canvas text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
                Four numbers <span className="italic font-serif text-lime">that shape the problem.</span>
              </h2>
            </div>
            <div className="md:col-span-4 text-canvas/70">
              Sourced from public World Bank &amp; GSMA data. Feel free to use these on your desk with attribution.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {REMITTANCE_STATS.map((s, i) => (
              <motion.div
                key={s.kpi}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-3xl border border-canvas/15 p-6 md:p-8 bg-white/[0.03] backdrop-blur-sm"
                data-testid={`press-stat-${i}`}
              >
                <div className="font-display font-medium text-lime text-5xl md:text-6xl tracking-tightest">
                  {s.kpi}
                </div>
                <div className="mt-4 text-canvas text-base leading-snug">{s.label}</div>
                <div className="mt-4 pt-4 border-t border-canvas/15 text-[10px] tracking-[0.22em] uppercase text-canvas/50">
                  Source · {s.source}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap gap-3">
            <a
              href="/media-kit.zip"
              download
              className="btn-accent"
              data-testid="press-download-media-kit"
            >
              <Download className="w-4 h-4" strokeWidth={2} />
              Download media kit (.zip)
            </a>
            <a
              href="/press-kit.pdf"
              download
              className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-canvas/25 text-canvas hover:bg-canvas/10 transition-colors"
              data-testid="press-download-kit"
            >
              <Download className="w-4 h-4" strokeWidth={2} />
              Press kit (PDF only)
            </a>
            <a
              href="/app-icon-1024.png"
              download
              className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-canvas/25 text-canvas hover:bg-canvas/10 transition-colors"
              data-testid="press-download-icon"
            >
              <Download className="w-4 h-4" />
              App icon (1024×1024 PNG)
            </a>
            <a
              href="mailto:press@instantxfer.com"
              className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-canvas/25 text-canvas hover:bg-canvas/10 transition-colors"
              data-testid="press-book-founder"
            >
              Book a founder for interview
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          {/* Media kit contents */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-3 text-canvas/70 text-sm">
            <div className="rounded-2xl border border-canvas/15 p-4">
              <div className="text-lime text-[10px] tracking-[0.22em] uppercase font-semibold">
                Logos
              </div>
              <div className="mt-2 leading-relaxed">
                Lockup SVG · mark SVG · 32/64/192 favicons
              </div>
            </div>
            <div className="rounded-2xl border border-canvas/15 p-4">
              <div className="text-lime text-[10px] tracking-[0.22em] uppercase font-semibold">
                iOS icons
              </div>
              <div className="mt-2 leading-relaxed">
                1024 light · dark · tinted (App Store Connect ready)
              </div>
            </div>
            <div className="rounded-2xl border border-canvas/15 p-4">
              <div className="text-lime text-[10px] tracking-[0.22em] uppercase font-semibold">
                Android icons
              </div>
              <div className="mt-2 leading-relaxed">
                Launcher 512 · maskable / adaptive 512
              </div>
            </div>
            <div className="rounded-2xl border border-canvas/15 p-4">
              <div className="text-lime text-[10px] tracking-[0.22em] uppercase font-semibold">
                Brand
              </div>
              <div className="mt-2 leading-relaxed">
                Palette JSON · type stack · voice · press PDF
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder digest preview — journalist read-only look */}
      <FounderDigestPreview />

      <WaitlistCTA />
    </>
  );
}


function FounderDigestPreview() {
  return (
    <section className="py-24 md:py-32 bg-surface" data-testid="press-digest-preview">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-12">
          <div className="md:col-span-8">
            <div className="label-eyebrow">The Sunday digest</div>
            <h2 className="mt-3 font-display font-medium text-forest text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
              What Founders <span className="italic font-serif">actually receive.</span>
            </h2>
          </div>
          <div className="md:col-span-4 text-ink/70">
            Every Sunday, our first hundred members get a three-number recap
            and one story from the week. This is the exact template — no
            personal data, just the shape.
          </div>
        </div>

        <div className="relative rounded-3xl border border-forest/15 bg-canvas overflow-hidden shadow-[0_28px_80px_-40px_rgba(74,29,150,0.4)]">
          <div className="flex items-center gap-2 border-b border-forest/10 px-5 py-3 bg-white/60 backdrop-blur">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-ink/15" />
              <span className="w-2.5 h-2.5 rounded-full bg-ink/15" />
              <span className="w-2.5 h-2.5 rounded-full bg-ink/15" />
            </div>
            <div className="flex-1 flex items-center gap-2 text-xs text-ink/55 truncate">
              <Mail className="w-3.5 h-3.5" strokeWidth={1.8} />
              <span className="font-mono truncate">
                founder@instantxfer.com · Founder digest · Sunday
              </span>
            </div>
            <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-forest bg-lime px-2 py-0.5 rounded">
              Sample
            </span>
          </div>
          <iframe
            src={`${API}/api/press/digest-preview`}
            title="Sunday Founder Digest — sample"
            className="w-full h-[720px] bg-canvas"
            loading="lazy"
            data-testid="press-digest-iframe"
          />
        </div>
      </div>
    </section>
  );
}
